/**
 * Iter 196 · Hot WA Threads SLA tile for Portfolio Pulse.
 *
 * Unassigned WhatsApp threads whose last inbound was within the last N
 * minutes (default 60m). Deep-links straight into the WA inbox with the
 * ref query param so the operator lands on the specific conversation.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Flame, MessageCircle, Loader2, RefreshCw, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { fmtRelative } from "@/lib/format";

export default function WaHotThreadsCard({ wsId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [window, setWindow] = useState(60);

  const load = async (minutes = window) => {
    setLoading(true);
    try {
      const r = await api.get(`/workspaces/${wsId}/wa/threads/hot`, {
        params: { window_minutes: minutes, limit: 10 },
      });
      setData(r.data);
    } catch {
      setData({ rows: [], unassigned_count: 0, total_in_window: 0 });
    } finally { setLoading(false); }
  };

  useEffect(() => { if (wsId) load(window); /* eslint-disable-next-line */ }, [wsId, window]);

  if (loading && !data) {
    return (
      <div className="border border-border bg-card p-3 text-xs text-muted-foreground"
              data-testid="wa-hot-threads-loading">Loading hot WhatsApp threads…</div>
    );
  }

  const rows = data?.rows || [];
  const totalInWindow = data?.total_in_window || 0;
  const unassignedCount = data?.unassigned_count || 0;
  const severity = unassignedCount >= 5 ? "high" : unassignedCount >= 1 ? "med" : "low";
  const tone = severity === "high"
    ? "border-rose-500 bg-rose-50"
    : severity === "med"
      ? "border-amber-500 bg-amber-50"
      : "border-emerald-500 bg-emerald-50";

  return (
    <div className="border border-border bg-card" data-testid="wa-hot-threads-card">
      <div className="p-3 flex items-center gap-2 border-b border-border">
        <Flame className="w-4 h-4 text-rose-600" />
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
          Hot WhatsApp threads · unassigned
        </div>
        <div className="ml-auto flex items-center gap-1">
          {[60, 180, 360].map((m) => (
            <button key={m} type="button"
                       onClick={() => setWindow(m)}
                       className={`text-[10px] uppercase tracking-widest font-semibold border px-2 py-0.5 ${window === m ? "border-primary text-primary bg-primary/10" : "border-border text-muted-foreground hover:border-primary"}`}
                       data-testid={`wa-hot-window-${m}`}>
              {m < 120 ? `${m}m` : `${m / 60}h`}
            </button>
          ))}
          <Button size="sm" variant="ghost" className="rounded-none h-7 px-2"
                    onClick={() => load(window)} disabled={loading}
                    data-testid="wa-hot-refresh">
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          </Button>
          <Link to="/app/marketing/wa-inbox" className="text-[10px] uppercase tracking-widest font-semibold text-primary hover:underline inline-flex items-center gap-0.5 px-2"
                  data-testid="wa-hot-open-inbox">
            Open inbox <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2 p-3">
        <div className={`border p-2 ${tone}`} data-testid="wa-hot-unassigned">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Unassigned · hot</div>
          <div className="text-2xl font-mono font-black">{unassignedCount}</div>
          <div className="text-[10px] text-muted-foreground">inbound in the last {window < 120 ? `${window}m` : `${window / 60}h`}</div>
        </div>
        <div className="border border-border bg-muted/20 p-2" data-testid="wa-hot-total">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Inbound activity</div>
          <div className="text-2xl font-mono font-black">{totalInWindow}</div>
          <div className="text-[10px] text-muted-foreground">contacts messaged in</div>
        </div>
        <div className="border border-border bg-muted/20 p-2" data-testid="wa-hot-severity">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">SLA status</div>
          <div className={`text-2xl font-mono font-black ${severity === "high" ? "text-rose-800" : severity === "med" ? "text-amber-800" : "text-emerald-800"}`}>
            {severity === "high" ? "URGENT" : severity === "med" ? "WATCH" : "CLEAR"}
          </div>
          <div className="text-[10px] text-muted-foreground">route to an agent</div>
        </div>
      </div>

      {rows.length > 0 && (
        <div className="border-t border-border max-h-56 overflow-y-auto divide-y divide-border">
          {rows.map((r) => (
            <Link key={r.contact_ref}
                     to={`/app/marketing/wa-inbox?ref=${encodeURIComponent(r.contact_ref)}`}
                     className="flex items-center gap-2 px-3 py-1.5 text-xs hover:bg-muted/30"
                     data-testid={`wa-hot-row-${r.contact_ref}`}>
              <MessageCircle className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="font-semibold truncate">{r.name}</div>
                <div className="text-[11px] text-muted-foreground truncate">{r.last_text || "(no preview)"}</div>
              </div>
              <div className="text-[10px] text-muted-foreground shrink-0">{fmtRelative(r.last_at)}</div>
              <ArrowRight className="w-3 h-3 text-muted-foreground shrink-0" />
            </Link>
          ))}
        </div>
      )}

      {rows.length === 0 && !loading && (
        <div className="text-xs text-muted-foreground italic p-4 text-center" data-testid="wa-hot-empty">
          No unassigned hot threads — every conversation has an owner. Nice.
        </div>
      )}
    </div>
  );
}
